import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';
import {
  ChevronLeft,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  Search,
  Filter,
  Package,
  Upload,
  Download,
  X,
  Image as ImageIcon,
  AlertCircle,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useMasterInventory, type MasterInventoryItemInput } from '../../hooks/useMasterInventory';
import { useMasterResidentialRooms } from '../../hooks/useResidentialRooms';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { MasterInventoryItem, MasterResidentialRoom, AddEquipmentEntry, QuoteType, ResidentialItemType } from '../../types/admin';
import { isPricedCategory, formatUnitPricing, parseUnitCostInput, parseRentalDaysInput } from '../../lib/inventoryPricing';
import { CustomDropdown, SearchableDropdown } from '../../components/ui/CustomDropdown';
import { InventoryFilterSheet, ActiveInventoryFilterPills, countActiveInventoryFilters, type InventoryFilters } from '../../components/admin/InventoryFilterSheet';
import { InventoryTable, InventoryViewSwitch, readStoredInventoryView, storeInventoryView, type InventoryView } from '../../components/admin/InventoryTable';

// ── Constants ──────────────────────────────────────────────────────────────

const ITEM_TYPES = ['Casegoods', 'Modular Component'] as const;
type ItemType = (typeof ITEM_TYPES)[number];

const TYPE_ORDER: Record<string, number> = {
  'Casegoods': 0,
  'Modular Component': 1,
  'Modular Equipment': 2,
};

function typeBadgeClass(type: string): string {
  switch (type) {
    case 'Casegoods': return 'bg-plum/10 text-plum';
    case 'Modular Component': return 'bg-ember/10 text-ember';
    case 'Modular Equipment': return 'bg-midnight/10 text-midnight';
    default: return 'bg-stone/10 text-slate';
  }
}

const RESIDENTIAL_TYPE_LABELS: Record<ResidentialItemType, string> = {
  customer_item: 'Customer Item',
  packing_item: 'Packing Item',
  equipment: 'Equipment',
};

function residentialTypeBadgeClass(type: ResidentialItemType): string {
  switch (type) {
    case 'customer_item': return 'bg-plum/10 text-plum';
    case 'packing_item': return 'bg-ember/10 text-ember';
    case 'equipment': return 'bg-midnight/10 text-midnight';
  }
}

const CSV_TEMPLATE_HEADERS = 'name,type,kz,description,notes,additionalManHours,isAccessorial,accessorialLabel,accessorialOriginManHours,accessorialDestinationManHours,quoteType,moveUnits,cubicFeet,weightInPounds,bulky,requiredDisassembly,residentialType,room,unitCost,rentalDays\n';
const CSV_TEMPLATE_FILENAME = 'master-inventory-template.csv';

// ── Helpers ────────────────────────────────────────────────────────────────

function downloadCsvTemplate() {
  const blob = new Blob([CSV_TEMPLATE_HEADERS], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = CSV_TEMPLATE_FILENAME;
  a.click();
  URL.revokeObjectURL(url);
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

// Images are stored as base64 data URLs inside Firestore documents (1 MiB hard limit).
// This function resizes/recompresses the image on the client so the data URL always
// fits. It tries JPEG quality 0.85 first; if the result is still too large it drops
// quality in steps, and if quality bottoms out it scales the dimensions down.
const MAX_IMAGE_DATA_URL_LENGTH = 950_000; // ~950 KB, leaves headroom for other fields

function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(objectUrl); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Failed to load image')); };
    img.src = objectUrl;
  });
}

async function resizeAndEncodeImage(file: File): Promise<string> {
  const img = await loadImageElement(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  // Cap starting dimensions at 1600 px on the longest side.
  const MAX_DIM = 1600;
  let width = img.naturalWidth;
  let height = img.naturalHeight;
  if (width > MAX_DIM || height > MAX_DIM) {
    const scale = MAX_DIM / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  let quality = 0.85;
  for (;;) {
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrl.length <= MAX_IMAGE_DATA_URL_LENGTH) return dataUrl;

    if (quality > 0.35) {
      quality = Math.max(0.35, quality - 0.15);
    } else {
      // Quality already at floor — shrink dimensions 25 % and reset quality.
      width = Math.round(width * 0.75);
      height = Math.round(height * 0.75);
      quality = 0.75;
      if (width < 100) return canvas.toDataURL('image/jpeg', 0.35); // safety valve
    }
  }
}

// ── Label component ────────────────────────────────────────────────────────

function FieldLabel({
  htmlFor,
  children,
  optional,
}: {
  htmlFor: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[11px] font-bold uppercase tracking-widest text-slate"
    >
      {children}
      {!optional && <span className="text-rose ml-0.5">*</span>}
      {optional && (
        <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>
      )}
    </label>
  );
}

// ── Add Equipment row ──────────────────────────────────────────────────────

interface AddEquipmentRowProps {
  entry: AddEquipmentEntry;
  allItems: MasterInventoryItem[];
  excludeId?: string;
  quoteType?: QuoteType;
  onChange: (entry: AddEquipmentEntry) => void;
  onRemove: () => void;
}

function AddEquipmentRow({ entry, allItems, excludeId, quoteType, onChange, onRemove }: AddEquipmentRowProps) {
  // Filter items by quote type: residential items show residential items, commercial show commercial
  const options = allItems
    .filter(i => i.id !== excludeId)
    .filter(i => {
      if (quoteType === 'residential') return i.quoteType === 'residential';
      return (i.quoteType ?? 'commercial') === 'commercial';
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(i => ({ value: i.id, label: i.name }));

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <SearchableDropdown
          options={options}
          value={entry.itemId}
          onChange={v => {
            const selected = allItems.find(i => i.id === v);
            if (selected) onChange({ ...entry, itemId: selected.id, itemName: selected.name });
          }}
          placeholder="Select item…"
        />
      </div>
      <input
        type="number"
        min="0"
        step="any"
        value={entry.quantity}
        onChange={e => {
          const raw = e.target.value;
          const val = parseFloat(raw);
          if (isNaN(val)) { onChange({ ...entry, quantity: 0 }); return; }
          const rounded = Math.round(val * 10000) / 10000;
          onChange({ ...entry, quantity: rounded });
        }}
        placeholder="Qty"
        className="w-20 h-11 px-2 rounded-lg border border-stone bg-white
                   text-small text-midnight placeholder:text-slate text-center
                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                   transition-colors duration-150"
      />
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border border-stone
                   text-slate hover:border-rose hover:text-rose active:bg-mist
                   transition-colors duration-150"
        aria-label="Remove row"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ── Inventory Item Form ────────────────────────────────────────────────────
// Mobile: bottom sheet. md+: centered dialog.

interface InventoryItemFormProps {
  initial?: MasterInventoryItem;
  allItems: MasterInventoryItem[];
  rooms?: MasterResidentialRoom[];
  onClose: () => void;
  /** Scroll the form to this section once it opens (used by the table's Add Equipment cell). */
  scrollTo?: 'addEquipment';
  onSave: (input: MasterInventoryItemInput) => Promise<unknown>;
}

function InventoryItemForm({ initial, allItems, rooms, onClose, scrollTo, onSave }: InventoryItemFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<string>(initial?.type ?? 'Casegoods');
  const [quoteType, setQuoteType] = useState<QuoteType>(initial?.quoteType ?? 'commercial');
  const [kz, setKz] = useState<string>(initial?.kz != null ? String(initial.kz) : '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [imageBase64, setImageBase64] = useState(initial?.imageBase64 ?? '');
  const [additionalManHours, setAdditionalManHours] = useState<string>(
    initial?.additionalManHours != null ? String(initial.additionalManHours) : '',
  );
  const [isAccessorial, setIsAccessorial] = useState(initial?.isAccessorial ?? false);
  const [accessorialLabel, setAccessorialLabel] = useState(initial?.accessorialLabel ?? '');
  const [accessorialOriginManHours, setAccessorialOriginManHours] = useState<string>(
    initial?.accessorialOriginManHours != null ? String(initial.accessorialOriginManHours) : '',
  );
  const [accessorialDestinationManHours, setAccessorialDestinationManHours] = useState<string>(
    initial?.accessorialDestinationManHours != null ? String(initial.accessorialDestinationManHours) : '',
  );
  const [addEquipment, setAddEquipment] = useState<AddEquipmentEntry[]>(
    initial?.addEquipment ?? [],
  );
  // Residential fields
  const [moveUnits, setMoveUnits] = useState<string>(initial?.moveUnits != null ? String(initial.moveUnits) : '');
  const [cubicFeet, setCubicFeet] = useState<string>(initial?.cubicFeet != null ? String(initial.cubicFeet) : '');
  const [weightInPounds, setWeightInPounds] = useState<string>(initial?.weightInPounds != null ? String(initial.weightInPounds) : '');
  const [bulky, setBulky] = useState(initial?.bulky ?? false);
  const [requiredDisassembly, setRequiredDisassembly] = useState(initial?.requiredDisassembly ?? false);
  // Category is required; new items start as Customer Item.
  const [residentialType, setResidentialType] = useState<ResidentialItemType>(initial?.residentialType ?? 'customer_item');
  const [roomId, setRoomId] = useState<string>(initial?.roomId ?? '');
  const [unitCost, setUnitCost] = useState<string>(initial?.unitCost != null ? String(initial.unitCost) : '');
  const [rentalDays, setRentalDays] = useState<string>(initial?.rentalDays != null ? String(initial.rentalDays) : '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Jump to the requested section after the modal paints.
  useEffect(() => {
    if (!scrollTo) return;
    const raf = requestAnimationFrame(() => {
      document.getElementById('inv-add-equipment')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [scrollTo]);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const isResidential = quoteType === 'residential';

  async function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeAndEncodeImage(file);
      setImageBase64(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read image file.');
    }
  }

  function addEquipmentRow() {
    setAddEquipment(prev => [...prev, { itemId: '', itemName: '', quantity: 1 }]);
  }

  function updateEquipmentRow(idx: number, entry: AddEquipmentEntry) {
    setAddEquipment(prev => prev.map((e, i) => (i === idx ? entry : e)));
  }

  function removeEquipmentRow(idx: number) {
    setAddEquipment(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!residentialType) { setError('Category is required.'); return; }
    if (isPricedCategory(residentialType) && parseRentalDaysInput(rentalDays) !== null && parseUnitCostInput(unitCost) === null) {
      setError('Unit cost is required when a rental length is set.'); return;
    }

    if (isResidential) {
      // Residential validation — no kz/type required
      for (const row of addEquipment) {
        if (!row.itemId) { setError('Each "Add Equipment" row must have an item selected.'); return; }
        if (row.quantity <= 0) { setError('Equipment quantities must be greater than 0.'); return; }
      }
      const validEquipment = addEquipment.filter(e => e.itemId);
      setError('');
      setSaving(true);
      try {
        const moveUnitsNum = moveUnits ? parseFloat(moveUnits) : undefined;
        const cubicFeetNum = cubicFeet ? parseFloat(cubicFeet) : undefined;
        const weightNum = weightInPounds ? parseFloat(weightInPounds) : undefined;
        await onSave({
          name: name.trim(),
          type: 'Casegoods', // default for residential
          kz: 0,
          description: description.trim() || undefined,
          notes: notes.trim() || undefined,
          imageBase64: imageBase64 || undefined,
          quoteType: 'residential',
          moveUnits: moveUnitsNum && !isNaN(moveUnitsNum) ? moveUnitsNum : undefined,
          cubicFeet: cubicFeetNum && !isNaN(cubicFeetNum) ? cubicFeetNum : undefined,
          weightInPounds: weightNum && !isNaN(weightNum) ? weightNum : undefined,
          bulky,
          requiredDisassembly,
          residentialType,
          roomId: roomId || undefined,
          unitCost: isPricedCategory(residentialType) ? parseUnitCostInput(unitCost) : null,
          rentalDays: isPricedCategory(residentialType) ? parseRentalDaysInput(rentalDays) : null,
          addEquipment: validEquipment.length > 0 ? validEquipment : undefined,
        });
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save item.');
        setSaving(false);
      }
      return;
    }

    // Commercial validation
    // KZ is optional: blank saves as 0
    const kzNum = kz.trim() ? parseFloat(kz) : 0;
    if (isNaN(kzNum) || kzNum < 0) { setError('KZ must be a valid non-negative number.'); return; }

    for (const row of addEquipment) {
      if (!row.itemId) { setError('Each "Add Equipment" row must have an item selected.'); return; }
      if (row.quantity <= 0) { setError('Equipment quantities must be greater than 0.'); return; }
    }

    if (isAccessorial) {
      if (!accessorialLabel.trim()) { setError('Accessorial label is required when accessorial service is enabled.'); return; }
      const originNum = accessorialOriginManHours ? parseFloat(accessorialOriginManHours) : 0;
      const destNum = accessorialDestinationManHours ? parseFloat(accessorialDestinationManHours) : 0;
      if (isNaN(originNum) || originNum < 0) { setError('Origin man hours must be a valid non-negative number.'); return; }
      if (isNaN(destNum) || destNum < 0) { setError('Destination man hours must be a valid non-negative number.'); return; }
    } else {
      const manHoursNum = additionalManHours ? parseFloat(additionalManHours) : undefined;
      if (additionalManHours && (isNaN(manHoursNum!) || manHoursNum! < 0)) {
        setError('Additional Man Hours must be a valid non-negative number.');
        return;
      }
    }

    const validEquipment = addEquipment.filter(e => e.itemId);

    setError('');
    setSaving(true);
    try {
      const manHoursNum = !isAccessorial && additionalManHours ? parseFloat(additionalManHours) : undefined;
      const originNum = isAccessorial && accessorialOriginManHours ? parseFloat(accessorialOriginManHours) : undefined;
      const destNum = isAccessorial && accessorialDestinationManHours ? parseFloat(accessorialDestinationManHours) : undefined;
      await onSave({
        name: name.trim(),
        type: type as MasterInventoryItemInput['type'],
        kz: kzNum,
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
        imageBase64: imageBase64 || undefined,
        additionalManHours: isAccessorial ? undefined : manHoursNum,
        isAccessorial: isAccessorial || undefined,
        accessorialLabel: isAccessorial ? accessorialLabel.trim() : undefined,
        accessorialOriginManHours: isAccessorial ? (originNum ?? 0) : undefined,
        accessorialDestinationManHours: isAccessorial ? (destNum ?? 0) : undefined,
        addEquipment: validEquipment.length > 0 ? validEquipment : undefined,
        quoteType,
        residentialType,
        unitCost: isPricedCategory(residentialType) ? parseUnitCostInput(unitCost) : null,
        rentalDays: isPricedCategory(residentialType) ? parseRentalDaysInput(rentalDays) : null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save item.');
      setSaving(false);
    }
  }

  return (
    /* Mobile: slides up from bottom. md+: centered dialog. */
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[92vh] overflow-y-auto
                      md:rounded-2xl md:max-w-lg md:w-full md:max-h-[85vh] md:pb-6">
        {/* Drag handle — mobile only */}
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">
            {initial ? 'Edit Item' : 'Add Item'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/*
          Mobile: single-column.
          md+: 2-column grid — KZ + Man Hours share a row; everything else full-width.
        */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-x-5 md:gap-y-4">
          {error && (
            <div className="md:col-span-2 flex items-start gap-2 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">{error}</p>
            </div>
          )}

          {/* Name — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="inv-name">Name</FieldLabel>
            <input
              id="inv-name"
              type="text"
              autoComplete="off"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Standard Desk"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Move type — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="inv-quotetype">Move type</FieldLabel>
            <div className="flex bg-mist rounded-lg p-1 gap-1">
              {(['commercial', 'residential'] as const).map(qt => (
                <button
                  key={qt}
                  type="button"
                  onClick={() => setQuoteType(qt)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                    ${quoteType === qt
                      ? 'bg-white text-midnight shadow-brand-sm'
                      : 'text-slate hover:text-midnight'
                    }`}
                >
                  {qt === 'commercial' ? 'Commercial' : 'Residential'}
                </button>
              ))}
            </div>
          </div>

          {/* Item Category + Type — side-by-side dropdowns on every viewport */}
          <div className="grid grid-cols-[1fr_1.25fr] gap-3 md:col-span-2">
            <div className={`flex flex-col gap-1.5 justify-end ${isResidential ? 'col-span-2' : ''}`}>
              <FieldLabel htmlFor="inv-category">Category</FieldLabel>
              <CustomDropdown<ResidentialItemType>
                id="inv-category"
                value={residentialType}
                onChange={setResidentialType}
                options={[
                  { value: 'customer_item', label: 'Customer Item' },
                  { value: 'packing_item', label: 'Packing Item' },
                  { value: 'equipment', label: 'Equipment' },
                ]}
              />
            </div>
            {!isResidential && (
              <div className="flex flex-col gap-1.5 justify-end">
                <FieldLabel htmlFor="inv-type">Type</FieldLabel>
                <CustomDropdown
                  id="inv-type"
                  value={type}
                  onChange={setType}
                  options={ITEM_TYPES.map(t => ({ value: t, label: t }))}
                />
              </div>
            )}
          </div>

          {/* Pricing — packing items and equipment only; directly below the category/type row */}
          {isPricedCategory(residentialType) && (
            <>
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="inv-unit-cost" optional>Unit Cost ($)</FieldLabel>
                <input
                  id="inv-unit-cost"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={unitCost}
                  onChange={e => setUnitCost(e.target.value)}
                  placeholder="e.g. 3.00"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="inv-rental-days" optional>Rental Length (days)</FieldLabel>
                <input
                  id="inv-rental-days"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  value={rentalDays}
                  onChange={e => setRentalDays(e.target.value)}
                  placeholder="Blank if purchased"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>
              <p className="text-[12px] text-slate -mt-2 md:col-span-2">
                {formatUnitPricing(parseUnitCostInput(unitCost), parseRentalDaysInput(rentalDays))
                  ?? 'Leave rental length blank for items that are purchased or consumed rather than returned.'}
              </p>
            </>
          )}

          {/* Room selector — residential only, right after quote type */}
          {isResidential && rooms && rooms.length > 0 && (
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <FieldLabel htmlFor="inv-room" optional>Room</FieldLabel>
              <SearchableDropdown
                id="inv-room"
                value={roomId}
                onChange={setRoomId}
                placeholder="No room assigned"
                options={[
                  { value: '', label: 'No room assigned' },
                  ...rooms.map(r => ({ value: r.id, label: r.name })),
                ]}
              />
            </div>
          )}

          {/* ── Commercial-specific fields ── */}
          {!isResidential && (
            <>
              {/* KZ — left column */}
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="inv-kz" optional>KZ Value</FieldLabel>
                <input
                  id="inv-kz"
                  type="number"
                  min="0"
                  step="0.01"
                  value={kz}
                  onChange={e => setKz(e.target.value)}
                  placeholder="0"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>

              {/* Additional Man Hours — right column (only when NOT accessorial) */}
              {!isAccessorial && (
                <div className="flex flex-col gap-1.5">
                  <FieldLabel htmlFor="inv-manhours" optional>Additional Man Hours</FieldLabel>
                  <input
                    id="inv-manhours"
                    type="number"
                    min="0"
                    step="0.01"
                    value={additionalManHours}
                    onChange={e => setAdditionalManHours(e.target.value)}
                    placeholder="e.g. 0.5"
                    className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                               text-small text-midnight placeholder:text-slate
                               focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                               transition-colors duration-150"
                  />
                </div>
              )}

              {/* Accessorial Service toggle — full width */}
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAccessorial}
                    onChange={e => setIsAccessorial(e.target.checked)}
                    className="w-5 h-5 rounded border-stone text-plum focus:ring-plum/20"
                  />
                  <span className="text-small text-midnight font-medium">Accessorial Service</span>
                </label>
              </div>

              {/* Accessorial fields — only when toggle is ON */}
              {isAccessorial && (
                <>
                  <div className="flex flex-col gap-1.5 md:col-span-2">
                    <FieldLabel htmlFor="inv-acc-label">Accessorial Label</FieldLabel>
                    <input
                      id="inv-acc-label"
                      type="text"
                      autoComplete="off"
                      value={accessorialLabel}
                      onChange={e => setAccessorialLabel(e.target.value)}
                      placeholder='e.g. "Glass Bonding", "Special Crating"'
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="inv-acc-origin">Origin Man Hours</FieldLabel>
                    <input
                      id="inv-acc-origin"
                      type="number"
                      min="0"
                      step="0.01"
                      value={accessorialOriginManHours}
                      onChange={e => setAccessorialOriginManHours(e.target.value)}
                      placeholder="0"
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="inv-acc-dest">Destination Man Hours</FieldLabel>
                    <input
                      id="inv-acc-dest"
                      type="number"
                      min="0"
                      step="0.01"
                      value={accessorialDestinationManHours}
                      onChange={e => setAccessorialDestinationManHours(e.target.value)}
                      placeholder="0"
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  </div>
                </>
              )}

            </>
          )}

          {/* ── Residential-specific fields ── */}
          {isResidential && (
            <>
              {/* Move Units — left column */}
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="inv-move-units" optional>Move Units</FieldLabel>
                <input
                  id="inv-move-units"
                  type="number"
                  min="0"
                  step="0.01"
                  value={moveUnits}
                  onChange={e => setMoveUnits(e.target.value)}
                  placeholder="e.g. 1.0"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>

              {/* Cubic Feet — right column */}
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="inv-cubic-feet" optional>Cubic Feet</FieldLabel>
                <input
                  id="inv-cubic-feet"
                  type="number"
                  min="0"
                  step="0.1"
                  value={cubicFeet}
                  onChange={e => setCubicFeet(e.target.value)}
                  placeholder="e.g. 12.5"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>

              {/* Weight — full width on mobile, left column on md+ */}
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="inv-weight" optional>Weight (lbs)</FieldLabel>
                <input
                  id="inv-weight"
                  type="number"
                  min="0"
                  step="0.1"
                  value={weightInPounds}
                  onChange={e => setWeightInPounds(e.target.value)}
                  placeholder="e.g. 50"
                  className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
              </div>

              {/* Toggles — right column */}
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bulky}
                    onChange={e => setBulky(e.target.checked)}
                    className="w-5 h-5 rounded border-stone text-plum focus:ring-plum/20"
                  />
                  <span className="text-small text-midnight font-medium">Bulky item</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requiredDisassembly}
                    onChange={e => setRequiredDisassembly(e.target.checked)}
                    className="w-5 h-5 rounded border-stone text-plum focus:ring-plum/20"
                  />
                  <span className="text-small text-midnight font-medium">Requires disassembly</span>
                </label>
              </div>
            </>
          )}

          {/* Description — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="inv-description" optional>Description</FieldLabel>
            <textarea
              id="inv-description"
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of the item…"
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate resize-none
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Notes — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="inv-notes" optional>Notes</FieldLabel>
            <textarea
              id="inv-notes"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes for estimators…"
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate resize-none
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Image — full width */}
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <FieldLabel htmlFor="inv-image" optional>Image</FieldLabel>
            {imageBase64 ? (
              <div className="flex items-center gap-3">
                <img
                  src={imageBase64}
                  alt="Item preview"
                  className="w-16 h-16 rounded-lg object-cover border border-stone"
                />
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className="h-8 px-3 rounded-lg border border-stone text-[13px] font-semibold text-slate
                               hover:border-plum hover:text-plum active:bg-mist transition-colors"
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageBase64('')}
                    className="h-8 px-3 rounded-lg border border-stone text-[13px] font-semibold text-slate
                               hover:border-rose hover:text-rose active:bg-mist transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="h-11 rounded-lg border-2 border-dashed border-stone
                           flex items-center justify-center gap-2
                           text-small font-semibold text-slate
                           hover:border-plum hover:text-plum active:bg-mist
                           transition-colors duration-150"
              >
                <ImageIcon size={16} />
                Upload Image
              </button>
            )}
            <input
              ref={imageInputRef}
              id="inv-image"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
          </div>

          {/* Add Equipment — full width */}
          <div id="inv-add-equipment" className="flex flex-col gap-2 md:col-span-2 scroll-mt-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
              Add Equipment
              <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>
            </p>
            <p className="text-[12px] text-slate leading-snug -mt-1">
              Items automatically added when this item is included in a job.
            </p>

            {addEquipment.length > 0 && (
              <div className="flex flex-col gap-2">
                {addEquipment.map((entry, idx) => (
                  <AddEquipmentRow
                    key={idx}
                    entry={entry}
                    allItems={allItems}
                    excludeId={initial?.id}
                    quoteType={quoteType}
                    onChange={updated => updateEquipmentRow(idx, updated)}
                    onRemove={() => removeEquipmentRow(idx)}
                  />
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={addEquipmentRow}
              disabled={allItems.filter(i => i.id !== initial?.id).length === 0}
              className="h-9 rounded-lg border border-dashed border-stone
                         flex items-center justify-center gap-1.5
                         text-[13px] font-semibold text-slate
                         hover:border-plum hover:text-plum active:bg-mist
                         disabled:opacity-40 disabled:cursor-not-allowed
                         transition-colors duration-150"
            >
              <Plus size={13} />
              Add Row
            </button>
          </div>

          {/* Submit — full width */}
          <button
            type="submit"
            disabled={saving}
            className="md:col-span-2 h-11 rounded-lg bg-plum text-white text-[13px] font-semibold
                       flex items-center justify-center gap-2
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 mt-1"
          >
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Item'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Inventory Item Card ────────────────────────────────────────────────────

interface InventoryItemCardProps {
  item: MasterInventoryItem;
  rooms?: MasterResidentialRoom[];
  onEdit: () => void;
  onDelete: () => void;
}

function InventoryItemCard({ item, rooms, onEdit, onDelete }: InventoryItemCardProps) {
  const isResidential = item.quoteType === 'residential';
  const roomName = isResidential && item.roomId && rooms
    ? rooms.find(r => r.id === item.roomId)?.name
    : undefined;
  const pricingLabel = formatUnitPricing(item.unitCost, item.rentalDays);

  return (
    <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm h-full">
      <div className="flex items-start gap-3">
        {/* Image or icon */}
        <div className="shrink-0">
          {item.imageBase64 ? (
            <img
              src={item.imageBase64}
              alt={item.name}
              className="w-10 h-10 rounded-lg object-cover border border-stone"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
              <Package size={18} className="text-plum" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-small text-midnight leading-snug">{item.name}</p>
            {isResidential ? (
              item.residentialType && (
                <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md
                  ${residentialTypeBadgeClass(item.residentialType)}`}>
                  {RESIDENTIAL_TYPE_LABELS[item.residentialType]}
                </span>
              )
            ) : (
              <>
                <span
                  className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md
                    ${typeBadgeClass(item.type as ItemType)}`}
                >
                  {item.type}
                </span>
                {item.residentialType && (
                  <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md
                    ${residentialTypeBadgeClass(item.residentialType)}`}>
                    {RESIDENTIAL_TYPE_LABELS[item.residentialType]}
                  </span>
                )}
              </>
            )}
          </div>
          {isResidential ? (
            <div className="flex items-center gap-2 flex-wrap text-[12px] text-slate mt-0.5">
              {item.moveUnits != null && <span>MU: <span className="font-semibold text-midnight font-mono">{item.moveUnits}</span></span>}
              {item.weightInPounds != null && <span>{item.moveUnits != null && '·'} {item.weightInPounds} lbs</span>}
              {item.cubicFeet != null && <span>· {item.cubicFeet} ft³</span>}
              {item.bulky && <span className="text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-ember/10 text-ember">Bulky</span>}
              {item.requiredDisassembly && <span className="text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-midnight/10 text-midnight">Disassembly</span>}
            </div>
          ) : (item.isAccessorial || item.additionalManHours != null) && (
            <p className="text-[12px] text-slate mt-0.5">
              {item.isAccessorial ? (
                <span className="ml-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-plum/10 text-plum">Accessorial</span>
                  <span className="ml-1">{item.accessorialLabel}</span>
                  <span className="ml-1 text-slate">
                    ({item.accessorialOriginManHours ?? 0}h origin, {item.accessorialDestinationManHours ?? 0}h dest)
                  </span>
                </span>
              ) : item.additionalManHours != null && (
                <span className="ml-2">
                  +{item.additionalManHours} hr{item.additionalManHours !== 1 ? 's' : ''}
                </span>
              )}
            </p>
          )}
          {pricingLabel && (
            <p className="text-[12px] text-slate mt-0.5">
              <span className="font-semibold text-midnight font-mono">{pricingLabel}</span>
            </p>
          )}
          {roomName && (
            <p className="text-[11px] text-slate mt-0.5">Room: {roomName}</p>
          )}
          {item.description && (
            <p className="text-[13px] text-slate mt-1 leading-snug line-clamp-2">
              {item.description}
            </p>
          )}
          {item.addEquipment && item.addEquipment.length > 0 && (
            <p className="text-[12px] text-slate mt-1">
              Also adds:{' '}
              {item.addEquipment
                .map(e => `${e.quantity}× ${e.itemName}`)
                .join(', ')}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:border-plum hover:text-plum active:bg-mist
                       transition-colors duration-150"
            aria-label={`Edit ${item.name}`}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:border-rose hover:text-rose active:bg-mist
                       transition-colors duration-150"
            aria-label={`Delete ${item.name}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bulk Upload Result Banner ──────────────────────────────────────────────

interface BulkResultBannerProps {
  created: number;
  updated: number;
  onClose: () => void;
}

function BulkResultBanner({ created, updated, onClose }: BulkResultBannerProps) {
  const parts: string[] = [];
  if (created > 0) parts.push(`${created} added`);
  if (updated > 0) parts.push(`${updated} updated`);
  const summary = parts.length > 0 ? parts.join(', ') : 'no changes';

  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-plum/8 border border-plum/20">
      <div className="flex-1 min-w-0">
        <p className="text-small font-semibold text-midnight">
          Bulk upload complete — {summary}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 w-6 h-6 flex items-center justify-center text-slate
                   hover:text-midnight transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ── Bulk Upload Dropdown ───────────────────────────────────────────────────

interface BulkUploadButtonProps {
  onUpload: (csv: string) => Promise<void>;
  onUploadJson: (items: Record<string, unknown>[]) => Promise<void>;
  onDownloadJson: () => Promise<void>;
}

function BulkUploadButton({ onUpload, onUploadJson, onDownloadJson }: BulkUploadButtonProps) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  async function handleCsvChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setOpen(false);
    setUploading(true);
    try {
      const text = await readFileAsText(file);
      await onUpload(text);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleJsonChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setOpen(false);
    setUploading(true);
    try {
      const text = await readFileAsText(file);
      const parsed = JSON.parse(text);
      const items = Array.isArray(parsed) ? parsed : parsed.items ?? parsed.data ?? [];
      await onUploadJson(items);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={uploading}
        className="h-11 px-3 sm:px-4 rounded-xl border border-stone bg-white
                   flex items-center gap-2
                   text-small font-semibold text-slate
                   hover:border-plum hover:text-plum active:bg-mist
                   disabled:opacity-50
                   transition-colors duration-150"
      >
        <Upload size={15} />
        <span className="hidden sm:inline">{uploading ? 'Uploading…' : 'Bulk Upload'}</span>
        <ChevronDown size={14} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-40 min-w-[200px]
                        bg-white border border-stone rounded-xl shadow-brand-md overflow-hidden">
          <button
            type="button"
            onClick={() => { setOpen(false); csvInputRef.current?.click(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Upload size={15} className="text-slate shrink-0" />
            Upload CSV File
          </button>
          <div className="h-px bg-stone mx-3" />
          <button
            type="button"
            onClick={() => { setOpen(false); jsonInputRef.current?.click(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Upload size={15} className="text-slate shrink-0" />
            Upload JSON File
          </button>
          <div className="h-px bg-stone mx-3" />
          <button
            type="button"
            onClick={() => { setOpen(false); downloadCsvTemplate(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Download size={15} className="text-slate shrink-0" />
            Download Template
          </button>
          <div className="h-px bg-stone mx-3" />
          <button
            type="button"
            onClick={() => { setOpen(false); onDownloadJson(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Download size={15} className="text-slate shrink-0" />
            Download All Inventory
          </button>
        </div>
      )}

      <input
        ref={csvInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={handleCsvChange}
      />
      <input
        ref={jsonInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={handleJsonChange}
      />
    </div>
  );
}

// ── Main Page Content ──────────────────────────────────────────────────────

function MasterInventoryContent() {
  const navigate = useNavigate();
  const { items, loading, error, createItem, updateItem, deleteItem, bulkUpload, downloadJson, bulkUploadJson } =
    useMasterInventory();
  const { rooms } = useMasterResidentialRooms();

  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<QuoteType>('commercial');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<ResidentialItemType | 'all'>('all');
  const [residentialTypeFilter, setResidentialTypeFilter] = useState<ResidentialItemType | 'all'>('all');
  const [roomFilter, setRoomFilter] = useState<string>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<MasterInventoryItem | null>(null);
  const [editSection, setEditSection] = useState<'addEquipment' | undefined>(undefined);
  // Cards or table; remembered across visits.
  const [listView, setListView] = useState<InventoryView>(readStoredInventoryView);
  function switchListView(v: InventoryView) { setListView(v); storeInventoryView(v); }
  function openEdit(item: MasterInventoryItem, section?: 'addEquipment') { setEditSection(section); setEditingItem(item); }
  const [deletingItem, setDeletingItem] = useState<MasterInventoryItem | null>(null);
  const [bulkResult, setBulkResult] = useState<{
    created: number;
    updated: number;
  } | null>(null);
  const [bulkError, setBulkError] = useState('');

  // Split items by quote type
  const isCommercialView = viewMode === 'commercial';

  // Type / category / room filters live in a sheet; only active ones show as pills.
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const inventoryFilters: InventoryFilters = {
    typeFilter,
    categoryFilter: isCommercialView ? categoryFilter : residentialTypeFilter,
    roomFilter,
  };
  const activeFilterCount = countActiveInventoryFilters(inventoryFilters, viewMode);
  function applyInventoryFilters(f: InventoryFilters) {
    setTypeFilter(f.typeFilter);
    if (isCommercialView) setCategoryFilter(f.categoryFilter); else setResidentialTypeFilter(f.categoryFilter);
    setRoomFilter(f.roomFilter);
  }
  const viewItems = items.filter(item =>
    isCommercialView
      ? (item.quoteType ?? 'commercial') === 'commercial'
      : item.quoteType === 'residential',
  );

  const filtered = viewItems
    .filter(item => {
      const matchesSearch =
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description?.toLowerCase().includes(search.toLowerCase());
      if (!matchesSearch) return false;
      if (isCommercialView) {
        const matchesType = typeFilter === 'all' || item.type === typeFilter;
        const matchesCategory = categoryFilter === 'all' || item.residentialType === categoryFilter;
        return matchesType && matchesCategory;
      }
      // Residential filters
      const matchesResType = residentialTypeFilter === 'all' || item.residentialType === residentialTypeFilter;
      const matchesRoom = roomFilter === 'all' || item.roomId === roomFilter;
      return matchesResType && matchesRoom;
    })
    .sort((a, b) => {
      if (isCommercialView) {
        const typeDiff = (TYPE_ORDER[a.type as ItemType] ?? 99) - (TYPE_ORDER[b.type as ItemType] ?? 99);
        return typeDiff !== 0 ? typeDiff : a.name.localeCompare(b.name);
      }
      return a.name.localeCompare(b.name);
    });

  // Group sorted items by type for section headers (commercial only)
  const groups: { type: string; items: MasterInventoryItem[] }[] = [];
  if (isCommercialView) {
    for (const item of filtered) {
      const last = groups[groups.length - 1];
      if (last && last.type === item.type) {
        last.items.push(item);
      } else {
        groups.push({ type: item.type as ItemType, items: [item] });
      }
    }
  }

  async function handleBulkUpload(csv: string) {
    setBulkError('');
    setBulkResult(null);
    try {
      const result = await bulkUpload(csv);
      setBulkResult(result);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Bulk upload failed.');
    }
  }

  async function handleBulkUploadJson(jsonItems: Record<string, unknown>[]) {
    setBulkError('');
    setBulkResult(null);
    try {
      const result = await bulkUploadJson(jsonItems);
      setBulkResult(result);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'JSON upload failed.');
    }
  }

  async function handleDownloadJson() {
    try {
      await downloadJson();
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Download failed.');
    }
  }

  return (
    <div className="px-4 pt-4 pb-8 md:px-8 md:pt-6 md:max-w-7xl md:mx-auto">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-2 md:mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Platform Admin Tools
      </button>

      <h1 className="font-display text-h1 text-midnight mb-3 md:mb-1">Master Inventory</h1>
      <p className="hidden md:block text-[13px] text-slate mb-5 leading-snug">
        Platform-wide catalog of inventory items. Every organization uses this list.
      </p>

      {/* Bulk result / error banners */}
      {bulkResult && (
        <div className="mb-4">
          <BulkResultBanner
            {...bulkResult}
            onClose={() => setBulkResult(null)}
          />
        </div>
      )}
      {bulkError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-small text-rose">{bulkError}</p>
          </div>
          <button
            type="button"
            onClick={() => setBulkError('')}
            className="shrink-0 w-6 h-6 flex items-center justify-center text-rose"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Actions row */}
      <div className="flex items-center gap-2 mb-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none"
          />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search items…"
            className="w-full h-11 pl-9 pr-3 rounded-xl border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        {/* Filter */}
        <button
          type="button"
          onClick={() => setShowFilterSheet(true)}
          className={`relative shrink-0 h-11 w-11 rounded-xl border flex items-center justify-center
            transition-colors duration-150
            ${activeFilterCount > 0
              ? 'border-plum bg-plum/5 text-plum'
              : 'border-stone bg-white text-slate hover:text-midnight hover:border-plum/40'}`}
          aria-label="Filter items"
        >
          <Filter size={16} />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-plum
                             flex items-center justify-center text-[9px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Bulk Upload */}
        <BulkUploadButton onUpload={handleBulkUpload} onUploadJson={handleBulkUploadJson} onDownloadJson={handleDownloadJson} />

        {/* Add Item */}
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="shrink-0 h-11 w-11 rounded-xl bg-plum text-white
                     flex items-center justify-center
                     hover:bg-rose active:opacity-80
                     transition-colors duration-150"
          aria-label="Add inventory item"
        >
          <Plus size={20} />
        </button>
      </div>

      {/* Commercial / Residential toggle + cards / table switch */}
      <div className="flex items-stretch gap-2 mb-2 md:mb-4">
        <div className="flex-1 flex bg-mist rounded-lg p-0.5 gap-1 md:p-1">
          {(['commercial', 'residential'] as const).map(qt => (
            <button
              key={qt}
              type="button"
              onClick={() => { setViewMode(qt); setSearch(''); }}
              className={`flex-1 h-11 md:h-auto md:py-2.5 text-[13px] md:text-sm font-semibold rounded-md transition-all duration-150
                ${viewMode === qt
                  ? 'bg-white text-midnight shadow-brand-sm'
                  : 'text-slate hover:text-midnight'
                }`}
            >
              {qt === 'commercial' ? 'Commercial' : 'Residential'}
            </button>
          ))}
        </div>
        <InventoryViewSwitch value={listView} onChange={switchListView} />
      </div>

      {/* Active filters — only the ones narrowing the view */}
      <ActiveInventoryFilterPills
        viewMode={viewMode}
        filters={inventoryFilters}
        rooms={rooms}
        onChange={applyInventoryFilters}
      />

      {showFilterSheet && (
        <InventoryFilterSheet
          viewMode={viewMode}
          filters={inventoryFilters}
          typeOptions={ITEM_TYPES}
          rooms={rooms}
          onApply={applyInventoryFilters}
          onClose={() => setShowFilterSheet(false)}
        />
      )}

      {/* List / Grid */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load inventory</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-14 h-14 rounded-full bg-mist flex items-center justify-center">
            <Package size={24} className="text-slate" />
          </div>
          <p className="text-small text-slate">No items yet. Add one or bulk upload a CSV.</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && filtered.length === 0 && (
        <div className="flex flex-col items-center py-12 gap-2 text-center">
          <p className="text-small text-slate">No items match your search.</p>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3 md:mb-4">
            {filtered.length} item{filtered.length !== 1 ? 's' : ''}
            {search && ` matching "${search}"`}
          </p>
          {listView === 'table' ? (
            <InventoryTable
              items={filtered}
              viewMode={viewMode}
              typeOptions={ITEM_TYPES}
              onUpdate={(item, patch) => updateItem(item.id, patch)}
              onEdit={openEdit}
              filters={inventoryFilters}
              onFiltersChange={applyInventoryFilters}
            />
          ) : isCommercialView ? (
            <div className="flex flex-col gap-6">
              {groups.map(group => (
                <section key={group.type}>
                  <div className="flex items-center gap-3 mb-3">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate shrink-0">
                      {group.type}
                    </p>
                    <div className="flex-1 h-px bg-stone" />
                    <span className="shrink-0 text-[11px] text-slate">{group.items.length}</span>
                  </div>
                  <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {group.items.map(item => (
                      <li key={item.id}>
                        <InventoryItemCard
                          item={item}
                          onEdit={() => setEditingItem(item)}
                          onDelete={() => setDeletingItem(item)}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          ) : (
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map(item => (
                <li key={item.id}>
                  <InventoryItemCard
                    item={item}
                    rooms={rooms}
                    onEdit={() => setEditingItem(item)}
                    onDelete={() => setDeletingItem(item)}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Add form */}
      {showAddForm && (
        <InventoryItemForm
          allItems={items}
          rooms={rooms}
          onClose={() => setShowAddForm(false)}
          onSave={createItem}
        />
      )}

      {/* Edit form */}
      {editingItem && (
        <InventoryItemForm
          initial={editingItem}
          allItems={items}
          rooms={rooms}
          scrollTo={editSection}
          onClose={() => { setEditingItem(null); setEditSection(undefined); }}
          onSave={input => updateItem(editingItem.id, input)}
        />
      )}

      {/* Delete confirm */}
      <ConfirmModal
        isOpen={!!deletingItem}
        title="Delete Item"
        message={`Are you sure you want to delete "${deletingItem?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deletingItem) deleteItem(deletingItem.id);
          setDeletingItem(null);
        }}
        onClose={() => setDeletingItem(null)}
      />
    </div>
  );
}

// ── Page (guards IOMI admin access) ───────────────────────────────────────

export function MasterInventoryPage() {
  const { currentUser, loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!currentUser?.isPlatformAdmin && IS_FIREBASE_CONFIGURED) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">Platform Admin access required.</p>
      </div>
    );
  }

  return <MasterInventoryContent />;
}
